import { z } from 'zod';

export const MovimentacaoTipoSchema = z.enum([
  'CREDITO_PENDENTE',
  'LIBERACAO',
  'DEBITO',
  'TAXA_PLATAFORMA',
  'ESTORNO',
  // Phase 21 v1.4 — gap closure. Empresa-side refund on evento cancel.
  // Distinct from ESTORNO (profissional-side LIBERACAO reversal, Phase 10).
  // Mirrors prisma/schema.prisma MovimentacaoTipo (D-01 in 21-CONTEXT.md).
  'ESTORNO_EMPRESA',
  // Phase 27 v1.6 — Wallet prepago (REQ-WALLET-01). D-03 in 27-CONTEXT.md.
  // DEPOSITO         : PIX-in topup credits empresa carteira (positive).
  // DEBITO_EVENTO    : pay-with-saldo debits empresa carteira (negative).
  // ESTORNO_WALLET_EMPRESA : cancel-after-pay-com-saldo re-credits carteira
  //                          (positive; distinct from ESTORNO_EMPRESA which
  //                           remains the PIX-out refund tipo).
  'DEPOSITO',
  'DEBITO_EVENTO',
  'ESTORNO_WALLET_EMPRESA',
]);
export const MovimentacaoStatusSchema = z.enum(['PENDENTE', 'LIBERADA', 'CANCELADA']);
export const OwnerTypeSchema = z.enum(['EMPRESA', 'PROFISSIONAL', 'PLATAFORMA']);

export const MovimentacaoDtoSchema = z.object({
  id: z.string(),
  tipo: MovimentacaoTipoSchema,
  status: MovimentacaoStatusSchema,
  valorCents: z.string(), // BigInt serialized as string
  descricao: z.string(),
  referencia: z.record(z.string(), z.unknown()).nullable(),
  criadoEm: z.string(),
});

export const CarteiraDtoSchema = z.object({
  id: z.string(),
  ownerType: OwnerTypeSchema,
  saldoDisponivelCents: z.string(),
  saldoAReceberCents: z.string(),
});

export const ExtratoFilterSchema = z.object({
  tipo: MovimentacaoTipoSchema.optional(),
  // Phase 28 v1.6 — REQ-EXTRATO-01 (D-01). Orthogonal to `tipo` (a movimentacao
  // can be CREDITO_PENDENTE+PENDENTE, or LIBERACAO+LIBERADA, or ESTORNO_*+
  // CANCELADA). Do NOT encode status via tipo (Pitfall 6).
  status: MovimentacaoStatusSchema.optional(),
  dataDe: z.iso.datetime().optional(),
  dataAte: z.iso.datetime().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(50),
});

// Phase 28 v1.6 — REQ-EXTRATO-01 (D-03). Recibo detail DTO. Bruto/taxa/liquido
// are *derived* server-side per tipo (RESEARCH "Bruto/taxa/líquido derivation"
// table). actorId/actorNome are raw — frontend MUST apply renderActorName
// for the LGPD anonymization fallback (anti-pattern: storing pre-rendered
// helper output as the actor name).
export const MovimentacaoDetailDtoSchema = z.object({
  id: z.string(),
  tipo: MovimentacaoTipoSchema,
  status: MovimentacaoStatusSchema,
  valorCents: z.string(),
  brutoCents: z.string(),
  taxaCents: z.string(),
  liquidoCents: z.string(),
  descricao: z.string(),
  referencia: z.record(z.string(), z.unknown()).nullable(),
  criadoEm: z.string(),
  eventoId: z.string().nullable(),
  eventoNome: z.string().nullable(),
  chargeId: z.string().nullable(),
  transferId: z.string().nullable(),
  candidaturaId: z.string().nullable(),
  actorId: z.string().nullable(),
  actorNome: z.string().nullable(),
});

export const ExtratoResponseSchema = z.object({
  carteira: CarteiraDtoSchema,
  movimentacoes: z.array(MovimentacaoDtoSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  hasMore: z.boolean(),
});

export const SettlementResponseSchema = z.object({
  eventoId: z.string(),
  movimentacoesCriadas: z.number(),
  totalLiberadoCents: z.string(),
  totalDebitoCents: z.string(),
  taxaPlataformaCents: z.string(),
  idempotent: z.boolean(),
});

export type MovimentacaoDto = z.infer<typeof MovimentacaoDtoSchema>;
export type MovimentacaoDetailDto = z.infer<typeof MovimentacaoDetailDtoSchema>;
export type CarteiraDto = z.infer<typeof CarteiraDtoSchema>;
export type ExtratoFilter = z.infer<typeof ExtratoFilterSchema>;
export type ExtratoResponse = z.infer<typeof ExtratoResponseSchema>;
export type SettlementResponse = z.infer<typeof SettlementResponseSchema>;

// ---------- Phase 32 — Cursor pagination (DEBT-04) ----------

/**
 * Query-param contract for GET /carteira/extrato/page.
 * cursor: opaque HMAC-signed token from a previous response; absent on first page.
 * limit: coerced from query string; defaults to 20; clamped to 100 server-side.
 * tipo/status/dataDe/dataAte: same orthogonal filters as ExtratoFilterSchema but
 * without the page/limit offset fields.
 */
export const ExtratoPageQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  tipo: MovimentacaoTipoSchema.optional(),
  status: MovimentacaoStatusSchema.optional(),
  dataDe: z.iso.datetime().optional(),
  dataAte: z.iso.datetime().optional(),
});

/**
 * Response shape for GET /carteira/extrato/page.
 * Replaces ExtratoResponse's page/total/hasMore with cursor-based nextCursor.
 */
export const ExtratoPageResponseSchema = z.object({
  carteira: CarteiraDtoSchema,
  movimentacoes: z.array(MovimentacaoDtoSchema),
  nextCursor: z.string().nullable(),
});

export type ExtratoPageQuery = z.infer<typeof ExtratoPageQuerySchema>;
export type ExtratoPageResponse = z.infer<typeof ExtratoPageResponseSchema>;
